import {
  ListingModel,
  ListingPublicDataModel,
  ListingPrivateDataModel,
  ListingStockModel,
  PriceModel,
} from '@loavies/c2c-models'

import { mapImage } from './image-mappers'
import { mapUser } from './user-mappers'
import { mapPrice } from './price-mappers'
import { mapDiscount } from './discount-mappers'

/**
 * @param {Array<Object>} listings
 * @return {loavies.models.listing.ListingModel[]}
 */
export function mapListings(listings) {
  return listings.map(listing => mapListing(listing))
}

/**
 * @param {Object} listing
 * @return {loavies.models.listing.ListingModel}
 */
export function mapListing(listing) {
  return new ListingModel({
    id: listing.id.uuid,
    title: listing.attributes.title,
    description: listing.attributes.description,
    price: listing.attributes.price ? mapPrice(listing.attributes.price) : new PriceModel({ amount: 0 }),
    images: listing.images?.map(image => mapImage(image)),
    publicData: mapPublicData(listing.attributes.publicData),
    privateData: listing.attributes.privateData ? mapPrivateData(listing.attributes.privateData) : null,
    state: listing.attributes.state,
    createdAt: listing.attributes.createdAt,
    author: listing.author ? mapUser(listing.author) : null,
    stock: listing.currentStock ? mapListingStock(listing.currentStock.attributes) : null,
  })
}

/**
 * @param {Object} publicData
 * @returns {loavies.models.listing.ListingPublicDataModel}
 */
export function mapPublicData(publicData) {
  return new ListingPublicDataModel({
    productGroup: publicData?.productGroup,
    color: publicData?.color,
    size: publicData?.size,
    condition: publicData?.condition,
    timesWatched: publicData?.timesWatched ?? 0,
    timesShared: publicData?.timesShared ?? 0,
    wishlistCount: publicData?.wishlistCount ?? 0,
    discount: publicData?.discount && mapDiscount(publicData.discount),
    labelId: publicData?.labelId,
    publishedAt: publicData?.publishedAt,
    shopIndex: publicData?.shopIndex,
    magentoSku: publicData?.magentoSku,
    material: publicData?.material,
  })
}

/**
 * @param {Object} privateData
 * @return {loavies.models.listing.ListingPrivateDataModel}
 */
export function mapPrivateData(privateData) {
  return new ListingPrivateDataModel({
    productId: privateData?.productId,
    productOriginalPrice: new PriceModel({
      amount: privateData?.productOriginalPrice?.amount,
      currency: privateData?.productOriginalPrice?.currency,
    }),
    productOriginalImages: privateData?.productOriginalImages,
  })
}

/**
 * @param {Object} stock
 * @return {loavies.models.listing.ListingStockModel}
 */
export function mapListingStock(stock) {
  return new ListingStockModel({
    quantity: stock.quantity,
  })
}

/**
 * @param {loavies.models.listing.ListingModel[]} listings
 * @param {boolean} [isDescending]
 * @return {loavies.models.listing.ListingModel[]} listings
 */
export function sortListingsByCreatedAt(listings, isDescending = true) {
  return listings.sort((a, b) => {
    if (a.createdAt < b.createdAt) {
      return isDescending ? 1 : -1
    }

    if (a.createdAt > b.createdAt) {
      return isDescending ? -1 : 1
    }

    return 0
  })
}

/**
 * @param {loavies.models.listing.ListingModel[]} listings
 * @param {boolean} [isDescending]
 * @return {loavies.models.listing.ListingModel[]} listings
 */
export function sortListingsByShopIndex(listings, isDescending = true) {
  return listings.sort((a, b) => {
    if (a.publicData.shopIndex < b.publicData.shopIndex) {
      return isDescending ? 1 : -1
    }

    if (a.publicData.shopIndex > b.publicData.shopIndex) {
      return isDescending ? -1 : 1
    }

    return 0
  })
}
